import type { Money } from '@rabbit/data/types';
import { calculateTotalValue } from './helpers';

type NumericLike = number | string;

type MoneyLike = (Partial<Money> & { amount?: NumericLike | null }) | undefined | null;

type MoneyAmountLike =
  | (Partial<Money> & { amount?: NumericLike | null })
  | undefined
  | null;

type CostItemWithRateAndVat = {
  time_spent?: string;
  rate?: MoneyAmountLike;
  VAT?: NumericLike | null;
};

type CostItemWithCostAndVat = {
  quantity?: NumericLike | null;
  cost?: MoneyAmountLike;
  VAT?: NumericLike | null;
};

type RepairWorkRegistryPart = {
  parts_quantity?: NumericLike | null;
  parts_cost?: MoneyAmountLike;
};

type RepairWorkRegistryItem = {
  parts_used_for_repair?: RepairWorkRegistryPart[];
};

type PostageRegistryItem = {
  outbound_postage_cost?: MoneyAmountLike;
};

type CostItemWithAmountAndVat = {
  amount?: MoneyAmountLike;
  VAT?: NumericLike | null;
};

export type ClaimFactsCostLike = {
  administrative_cost_data?: CostItemWithRateAndVat[];
  repair_time_data?: CostItemWithRateAndVat[];
  parts_used_cost_data?: CostItemWithCostAndVat[];
  repair_work_registry?: RepairWorkRegistryItem[];
  shipping_cost_data?: CostItemWithAmountAndVat[];
  postage_registry?: PostageRegistryItem[];
  travel_cost_data?: CostItemWithAmountAndVat[];
  other_cost_data?: CostItemWithAmountAndVat[];
};

function toNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const n = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function vatToNumber(vat: unknown): number {
  const n = toNumber(vat);
  return n > 0 ? n : 0;
}

function pushCurrency(currencies: Set<string>, currency?: string) {
  if (currency && String(currency).trim() !== '') currencies.add(String(currency));
}

function calculateAmountWithVatTotal(
  costData: CostItemWithAmountAndVat[],
  currencies: Set<string>
): number {
  for (const item of costData) {
    pushCurrency(currencies, item?.amount?.currency);
  }
  return costData.reduce((acc, item) => {
    const sub = toNumber(item?.amount?.amount);
    return acc + sub + (sub * vatToNumber(item?.VAT)) / 100;
  }, 0);
}

export function calcClaimCostsTotal(facts: ClaimFactsCostLike): {
  total: number;
  currencies: string[];
  primaryCurrency?: string;
} {
  const currencies = new Set<string>();

  // Administrative + assessment time (rate + VAT)
  const administrative_cost_data: CostItemWithRateAndVat[] =
    facts?.administrative_cost_data || [];
  for (const curr of administrative_cost_data) {
    pushCurrency(currencies, curr?.rate?.currency);
  }
  const administrativeTotal = administrative_cost_data.reduce(
    (acc: number, curr) =>
      acc +
      calculateTotalValue(
        curr?.time_spent || '0:00',
        toNumber(curr?.rate?.amount),
        vatToNumber(curr?.VAT)
      ),
    0
  );

  // Repair time (rate + VAT)
  const repair_time_data: CostItemWithRateAndVat[] = facts?.repair_time_data || [];
  for (const curr of repair_time_data) {
    pushCurrency(currencies, curr?.rate?.currency);
  }
  const repairTotal = repair_time_data.reduce(
    (acc: number, curr) =>
      acc +
      calculateTotalValue(
        curr?.time_spent || '0:00',
        toNumber(curr?.rate?.amount),
        vatToNumber(curr?.VAT)
      ),
    0
  );

  // Parts used (cost + VAT)
  const parts_used_cost_data: CostItemWithCostAndVat[] =
    facts?.parts_used_cost_data || [];
  for (const p of parts_used_cost_data) pushCurrency(currencies, p?.cost?.currency);
  const partsUsedTotal = parts_used_cost_data.reduce((acc: number, p) => {
    const qty = toNumber(p?.quantity);
    const unit = toNumber(p?.cost?.amount);
    const sub = qty * unit;
    return acc + sub + (sub * vatToNumber(p?.VAT)) / 100;
  }, 0);

  // Type1 parts used from repair_work_registry (no VAT in that structure)
  const repair_work_registry: RepairWorkRegistryItem[] =
    facts?.repair_work_registry || [];
  const type1PartsUsedTotal = repair_work_registry.reduce(
    (acc: number, work) => {
      const parts: RepairWorkRegistryPart[] = work?.parts_used_for_repair || [];
      for (const p of parts) pushCurrency(currencies, p?.parts_cost?.currency);
      return (
        acc +
        (parts || []).reduce((innerAcc: number, p) => {
          const qty = toNumber(p?.parts_quantity);
          const unit = toNumber(p?.parts_cost?.amount);
          return innerAcc + qty * unit;
        }, 0)
      );
    },
    0
  );

  // Shipping/postage (amount + VAT)
  const shipping_cost_data: CostItemWithAmountAndVat[] =
    facts?.shipping_cost_data || [];
  const shippingTotal = calculateAmountWithVatTotal(shipping_cost_data, currencies);

  // Type1 postage registry (no VAT in that structure)
  const postage_registry: PostageRegistryItem[] = facts?.postage_registry || [];
  for (const p of postage_registry) pushCurrency(currencies, p?.outbound_postage_cost?.currency);
  const type1PostageTotal = postage_registry.reduce((acc: number, p) => {
    const sub = toNumber(p?.outbound_postage_cost?.amount);
    return acc + sub;
  }, 0);

  // Travel (amount + VAT)
  const travel_cost_data: CostItemWithAmountAndVat[] =
    facts?.travel_cost_data || [];
  const travelTotal = calculateAmountWithVatTotal(travel_cost_data, currencies);

  // Other (amount + VAT)
  const other_cost_data: CostItemWithAmountAndVat[] = facts?.other_cost_data || [];
  const otherTotal = calculateAmountWithVatTotal(other_cost_data, currencies);

  const total =
    administrativeTotal +
    repairTotal +
    partsUsedTotal +
    type1PartsUsedTotal +
    shippingTotal +
    type1PostageTotal +
    travelTotal +
    otherTotal;

  const currenciesArr = Array.from(currencies);
  return {
    total,
    currencies: currenciesArr,
    primaryCurrency: currenciesArr[0],
  };
}

export function formatWarrantyCostForTable(warrantyCost: MoneyLike): {
  amount: number;
  currency?: string;
} {
  return {
    amount: toNumber((warrantyCost as any)?.amount),
    currency: (warrantyCost as any)?.currency,
  };
}

